import { useEffect, useState, useRef } from 'react'
import { X, LayoutDashboard, TrendingUp, Users, ShieldCheck, Sparkles } from 'lucide-react'

const DURATION = 15 // seconds

export default function WelcomeModal() {
  const [visible, setVisible] = useState(false)
  const [timeLeft, setTimeLeft] = useState(DURATION)
  const [closing, setClosing] = useState(false)
  const timerRef = useRef(null)
  const countRef = useRef(null)

  useEffect(() => {
    // Show only once per session (not every page reload)
    const shown = sessionStorage.getItem('welcomeShown')
    if (shown) return

    // Small delay so dashboard renders first
    const delay = setTimeout(() => {
      setVisible(true)
      sessionStorage.setItem('welcomeShown', 'true')

      // Countdown
      countRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(countRef.current)
            handleClose()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 600)

    return () => {
      clearTimeout(delay)
      clearInterval(countRef.current)
    }
  }, [])

  const handleClose = () => {
    clearInterval(countRef.current)
    setClosing(true)
    setTimeout(() => setVisible(false), 350)
  }

  if (!visible) return null

  const progress = ((DURATION - timeLeft) / DURATION) * 100

  const features = [
    { icon: TrendingUp,     color: '#4F46E5', bg: 'rgba(79,70,229,0.12)',  label: 'Live Analytics',     desc: 'Real-time KPIs & revenue' },
    { icon: Users,          color: '#10B981', bg: 'rgba(16,185,129,0.12)', label: 'User Management',    desc: 'Manage users & roles'     },
    { icon: ShieldCheck,    color: '#06B6D4', bg: 'rgba(6,182,212,0.12)',  label: 'Secure & Reliable',  desc: 'Enterprise-grade security' },
    { icon: LayoutDashboard,color: '#A855F7', bg: 'rgba(168,85,247,0.12)', label: 'Full Control',       desc: 'All tools in one place'   },
  ]

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(4, 6, 16, 0.72)',
          backdropFilter: 'blur(6px)',
          animation: closing ? 'wm-fade-out .35s ease forwards' : 'wm-fade-in .3s ease forwards',
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 2001,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem', pointerEvents: 'none',
      }}>
        <div style={{
          pointerEvents: 'auto',
          width: '100%', maxWidth: 520,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-active)',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(99,102,241,0.15)',
          animation: closing ? 'wm-slide-out .35s cubic-bezier(0.4,0,1,1) forwards' : 'wm-slide-in .4s cubic-bezier(0.16,1,0.3,1) forwards',
        }}>

          {/* Progress bar — top */}
          <div style={{ height: 3, background: 'var(--border-subtle)', position: 'relative' }}>
            <div style={{
              position: 'absolute', left: 0, top: 0, height: '100%',
              width: `${progress}%`,
              background: 'linear-gradient(90deg, var(--accent), var(--accent-hover))',
              transition: 'width 1s linear',
              borderRadius: '0 2px 2px 0',
            }}/>
          </div>

          {/* Hero section */}
          <div style={{
            position: 'relative', padding: '2rem 2rem 1.5rem',
            background: 'linear-gradient(135deg, rgba(79,70,229,0.14) 0%, rgba(6,182,212,0.08) 100%)',
            borderBottom: '1px solid var(--border-subtle)',
            overflow: 'hidden',
          }}>
            {/* Glow orbs */}
            <div style={{ position:'absolute', width:200, height:200, borderRadius:'50%', background:'rgba(79,70,229,0.18)', filter:'blur(50px)', top:-60, right:-40, pointerEvents:'none' }}/>
            <div style={{ position:'absolute', width:120, height:120, borderRadius:'50%', background:'rgba(6,182,212,0.12)', filter:'blur(35px)', bottom:-30, left:20, pointerEvents:'none' }}/>

            {/* Close + timer */}
            <div style={{ position:'absolute', top:14, right:14, display:'flex', alignItems:'center', gap:8 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, color: 'var(--text3)',
                background: 'var(--bg-raised)', border: '1px solid var(--border)',
                borderRadius: 20, padding: '3px 10px',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {timeLeft}s
              </span>
              <button
                onClick={handleClose}
                style={{
                  width: 28, height: 28, borderRadius: 8,
                  border: '1px solid var(--border)', background: 'var(--bg-raised)',
                  color: 'var(--text3)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color='var(--text1)'; e.currentTarget.style.background='var(--bg-hover)' }}
                onMouseLeave={e => { e.currentTarget.style.color='var(--text3)'; e.currentTarget.style.background='var(--bg-raised)' }}
                title="Close"
              >
                <X size={14}/>
              </button>
            </div>

            {/* Icon + greeting */}
            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16, position:'relative' }}>
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: 'linear-gradient(135deg, var(--accent), var(--accent-hover))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(79,70,229,0.4)',
                flexShrink: 0,
              }}>
                <Sparkles size={24} color="#fff" strokeWidth={1.8}/>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent-hover)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom: 3 }}>
                  Welcome back
                </div>
                <h2 style={{ fontSize: '1.375rem', fontWeight: 800, color: 'var(--text1)', letterSpacing: '-0.025em', lineHeight: 1.15 }}>
                  Good to see you, Admin! 👋
                </h2>
              </div>
            </div>

            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text2)', lineHeight: 1.7, position: 'relative' }}>
              Your dashboard is live and ready. Here's a quick look at what's waiting for you today — dive in whenever you're ready.
            </p>
          </div>

          {/* Feature grid */}
          <div style={{ padding: '1.25rem 2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {features.map(({ icon: Icon, color, bg, label, desc }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px',
                background: 'var(--bg-raised)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 10,
                transition: 'border-color .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor='var(--border)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--border-subtle)'}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={15} color={color} strokeWidth={1.8}/>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text1)', marginBottom: 1 }}>{label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{
            padding: '0 2rem 1.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <span style={{ fontSize: 11, color: 'var(--text4)' }}>
              Auto-closes in {timeLeft}s
            </span>
            <button
              onClick={handleClose}
              style={{
                background: 'var(--accent)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 22px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(79,70,229,0.35)',
                transition: 'all .15s',
                fontFamily: 'inherit',
              }}
              onMouseEnter={e => { e.currentTarget.style.background='var(--accent-hover)'; e.currentTarget.style.transform='translateY(-1px)' }}
              onMouseLeave={e => { e.currentTarget.style.background='var(--accent)'; e.currentTarget.style.transform='translateY(0)' }}
            >
              Let's Go →
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes wm-fade-in  { from { opacity:0 } to { opacity:1 } }
        @keyframes wm-fade-out { from { opacity:1 } to { opacity:0 } }
        @keyframes wm-slide-in {
          from { opacity:0; transform:translateY(28px) scale(.96) }
          to   { opacity:1; transform:translateY(0) scale(1) }
        }
        @keyframes wm-slide-out {
          from { opacity:1; transform:translateY(0) scale(1) }
          to   { opacity:0; transform:translateY(16px) scale(.97) }
        }
      `}</style>
    </>
  )
}